import {Button, Card, Col, Row, Select, Table, Tag} from "antd"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import React, {ReactElement, useCallback, useEffect, useMemo} from "react"
import {Loading} from "../../components/Loading/Loading"
import {getPSLeakDevices, setPSLeakDeviceFilter, setPSLeakDevices, setWSPSLeakDevice} from "../../store/psLeakDeviceReducer"
import {isActiveOptionsSelect, limitOptionsSelect} from "../../helps/optionsSelect"
import {NavLink, useHistory, useLocation} from "react-router-dom"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import {center} from "../../helps/tableHelper"
import {Helmet} from "react-helmet-async"
import {FormSelect} from "../../components/Form/FormSelect"
import {ID_SYSTEM_COMPANY} from "../../constants/constants"
import {useGetCompanies, useGetObjectGroups, useGetObjects} from "../../hooks/useFetchHooks"
import {getClassNameLocalLogLinkText} from "../../helps/getClassNameLocalLogLinkText"
import {PSLeakDeviceResponse} from "../../models/response"
import {FormInput} from "../../components/Form/FormInput"

export const PSLeakDevices = () =>
{
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const psLeakDevices = useSelector((state: StateType) => state.psLeakDeviceReducer.psLeakDevices)
    const filter = useSelector((state: StateType) => state.psLeakDeviceReducer.filter)
    const ws = useSelector((state: StateType) => state.psLeakDeviceReducer.ws)
    const controller = useMemo(() => new AbortController(), [])

    const getCompanyParams = useMemo(() => ({excludeSystem: true}), [])
    const companies = useGetCompanies(getCompanyParams)

    const objectGroupsQuery = useMemo(() => ({
        idCompany: filter.idCompany,
    }), [filter.idCompany])
    const objectGroups = useGetObjectGroups(objectGroupsQuery)

    const objectsQuery = useMemo(() => ({
        idCompany: filter.idCompany,
        idObjectGroup: filter.idObjectGroup,
    }), [filter.idCompany, filter.idObjectGroup])
    const objects = useGetObjects(objectsQuery)

    const columns = useMemo(() =>
    {
        const cols = []

        if (profile?.idCompany === ID_SYSTEM_COMPANY) cols.push({
            title: "Компания",
            dataIndex: "nameCompany",
            key: "nameCompany",
            align: center,
        })

        cols.push(
            {
                title: "Группа",
                dataIndex: "nameObjectGroup",
                key: "nameObjectGroup",
                align: center,
            },
            {
                title: "Объект",
                dataIndex: "nameObject",
                key: "nameObject",
                align: center,
            },
            {
                title: "Название",
                dataIndex: "name",
                key: "name",
                align: center,
            },
            {
                title: "UIN",
                dataIndex: "uin",
                key: "uin",
                align: center,
            },
            {
                title: "Версия",
                dataIndex: "version",
                key: "version",
                align: center,
            },
            {
                title: "Статус",
                dataIndex: "status",
                key: "status",
                render: (psLeakDevice: PSLeakDeviceResponse) =>
                {
                    const problems: ReactElement[] = [<Tag key="active" color={psLeakDevice.active ? "green" : "volcano"}
                                                           style={{marginRight: 0}}>{psLeakDevice.active ? "Активно" : "Не активно"}</Tag>]

                    if (psLeakDevice.active && psLeakDevice.offline)
                    {
                        problems.push(<Tag key="offline" color="volcano" style={{marginRight: 0}}>Не выходит на связь</Tag>)
                    }

                    if (psLeakDevice.lowMemoryNotification)
                    {
                        problems.push(<Tag key="lowMemoryNotification" color="gold" style={{marginRight: 0}}>Заканчивается память устройства</Tag>)
                    }

                    if (psLeakDevice.disableLocalData)
                    {
                        problems.push(<Tag key="disableLocalData" color="gold" style={{marginRight: 0}}>Не принимаются локальные данные</Tag>)
                    }

                    if (psLeakDevice.versionLocalNeedUpdate)
                    {
                        problems.push(<Tag key="versionLocalNeedUpdate" color="gold" style={{marginRight: 0}}>Требуется обновление версии</Tag>)
                    }

                    if (psLeakDevice.attributesServerDeprecated || psLeakDevice.attributesLocalDeprecated)
                    {
                        problems.push(<Tag key="attributesDeprecated" color="gold" style={{marginRight: 0}}>Атрибуты устарели</Tag>)
                    }

                    if (psLeakDevice.currentStatus?.length > 0)
                    {
                        psLeakDevice?.currentStatus?.forEach(i => problems.push(
                            <div key={`local_log_${i.id}`}>
                                <NavLink style={{fontSize: 13}}
                                         className={getClassNameLocalLogLinkText(i.idLogType)}
                                         to={`/psLeakDeviceLocalLog/${i.id}`}
                                         onClick={(e) => e.stopPropagation()}>
                                    {i.description}
                                </NavLink>
                            </div>,
                        ))
                    }

                    try
                    {
                        const parsed = JSON.parse(psLeakDevice.attributesLocal)

                        if (parsed?.enC === 0)
                            problems.push(<Tag key="enC" color="gold" style={{marginRight: 0}}>Контроль ХВС отключен</Tag>)

                        if (parsed?.enH === 0)
                            problems.push(<Tag key="enH" color="gold" style={{marginRight: 0}}>Контроль ГВС отключен</Tag>)
                    }
                    catch {

                    }

                    return (
                        <div className="table-cell-status">
                            {problems}
                        </div>
                    )
                },
                align: center,
            },
        )

        return cols
    }, [profile])

    useEffect(() =>
    {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter))
        {
            dispatch(setPSLeakDeviceFilter({...filterString}))
        }
        else if (location.search !== filterToString(filter))
        {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    const getPSLeakDevicesFunc = useCallback((withoutLoadingIndicator = false) =>
    {
        if (location?.search)
        {
            dispatch(getPSLeakDevices({
                ...filterFromString(location.search),
                withoutLoadingIndicator,
                controller,
            }))
        }
    }, [dispatch, location, controller])

    useEffect(() =>
    {
        getPSLeakDevicesFunc()

        return () =>
        {
            dispatch(setPSLeakDevices(null))
        }
    }, [dispatch, getPSLeakDevicesFunc])

    useEffect(() =>
    {
        return () =>
        {
            controller.abort()
        }
    }, [dispatch, controller])

    useEffect(() =>
    {
        if (profile?.idCompany !== ID_SYSTEM_COMPANY && filter?.idCompany !== profile?.idCompany)
        {
            history.replace({search: filterToString({...filter, idCompany: profile?.idCompany})})
        }
    }, [profile])

    useEffect(() =>
    {
        if (ws)
        {
            getPSLeakDevicesFunc(true)
        }
        dispatch(setWSPSLeakDevice(null))
    }, [dispatch, ws, getPSLeakDevicesFunc])

    const data = useMemo(() => psLeakDevices?.data?.map(i =>
    {
        return {
            key: i.id,
            id: i.id,
            name: i.name,
            nameObject: i.nameObject,
            nameObjectGroup: i.nameObjectGroup,
            nameCompany: i.nameCompany,
            uin: i.uin,
            version: i.versionLocal,
            status: i,
        }
    }), [psLeakDevices])

    const companyOptions = useMemo(() => companies?.data?.map(i => ({label: i.name, value: i.id})), [companies])
    const objectGroupsOptions = useMemo(() => objectGroups?.data?.map(i => ({label: i.name, value: i.id})), [objectGroups])
    const objectOptions = useMemo(() => objects?.data?.map(i => ({label: i.name, value: i.id})), [objects])

    return (
        <div className="tabled">
            <Helmet>
                <title>Оборудование</title>
            </Helmet>
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    <Card variant="borderless"
                          className="criclebox tablespace mb-24"
                          title={
                              <div className="table-title">
                                  <div>Оборудование</div>
                                  <Button onClick={() => history.push("/psLeakDeviceForm")} type="link" size="middle" style={{marginTop: 5}}>
                                      Добавить
                                  </Button>
                              </div>
                          }
                          extra={
                              <div className="table-title-extra">
                                  <FormInput placeholder="Поиск"
                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                             value={filter?.search}
                                             name="search"
                                             onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                             allowClear
                                  />

                                  {
                                      profile?.idCompany === ID_SYSTEM_COMPANY
                                          ? <FormSelect value={filter?.idCompany}
                                                        containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                        dropdownStyle={{minWidth: "min-content"}}
                                                        onChange={(value) => history.replace({
                                                            search: filterToString({
                                                                ...filter,
                                                                idCompany: value,
                                                                idObjectGroup: "",
                                                                idObject: "",
                                                                offset: 0,
                                                            }),
                                                        })}
                                                        options={companyOptions}
                                                        placeholder="Компания"
                                                        allowClear
                                                        placement="bottomRight"
                                          />
                                          : null
                                  }

                                  <FormSelect value={filter?.idObjectGroup}
                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) => history.replace({
                                                  search: filterToString({
                                                      ...filter,
                                                      idObjectGroup: value,
                                                      idObject: "",
                                                      offset: 0,
                                                  }),
                                              })}
                                              options={objectGroupsOptions}
                                              placeholder="Группа"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.idObject}
                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) => history.replace({
                                                  search: filterToString({
                                                      ...filter,
                                                      idObject: value,
                                                      offset: 0,
                                                  }),
                                              })}
                                              options={objectOptions}
                                              placeholder="Объект"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.status}
                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, status: value, offset: 0})})}
                                              options={[...isActiveOptionsSelect, {value: "2", label: "Требуют внимания"}]}
                                              placeholder="Статус"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.limit}
                                              containerStyle={{marginBottom: 0, width: "auto"}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                              options={limitOptionsSelect}
                                              placement="bottomRight"
                                  />
                              </div>
                          }
                    >
                        <div className="table-responsive">
                            {
                                psLeakDevices
                                    ? <Table columns={columns}
                                             dataSource={data}
                                             pagination={filter.limit ? {
                                                 position: psLeakDevices?.data?.length >= 10 ? ["bottomLeft", "topLeft"] : ["bottomLeft"],
                                                 pageSize: filter.limit,
                                                 hideOnSinglePage: true,
                                                 total: psLeakDevices.total,
                                                 current: filter?.offset / filter?.limit + 1,
                                                 onChange: (page, pageSize) =>
                                                 {
                                                     const obj = {
                                                         limit: pageSize,
                                                         offset: (page - 1) * pageSize,
                                                     }
                                                     history.replace({search: filterToString({...filter, ...obj})})
                                                 },
                                                 showSizeChanger: false,
                                             } : false}
                                             className="ant-border-space"
                                             onRow={({id}) => ({
                                                 onClick: () => history.push(`/psLeakDevice/${id}`),
                                                 onMouseDown: (e) => e.button === 1 && window.open(`/psLeakDevice/${id}`, '_blank', 'noopener,noreferrer')
                                             })}
                                             rowClassName={({status}) => status.deleted || !status.active
                                                 ? "table-row-deleted"
                                                 : status.active && status.offline
                                                     ? "table-row-danger"
                                                     : ""}
                                    />
                                    : <Loading/>
                            }
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}
